<template>
    <v-sheet ref="wrapper" class="fill-height d-flex flex-column">
        <h2 class="page-title page-title__primary">Скважины</h2>
        <p>Данные берутся из секций WELSPECS и COMPDAT</p>
        <div class="grid-wrapper" :style="{marginRight: sidebarWidth+'px'}">
            <slot name="grid" :wc="this"></slot>
        </div>
        <v-dialog v-model="dialog" max-width="650px">
            <well-edit v-model="editedItem"
                       :reservoir="reservoir"
                       :wells="wells"
                       :nodes="nodes"
                       :connections="connections"
                       :grid="gridProperties"
                       :input-constants="inputConstants"
                       :recalc-loading="recalcLoading"
                       @input-connections="setUpdatedConnections"
                       @delete="deleteEditedWell"
                       @recalc="fillWell"
                       @input="save"
                       @close="close"
            ></well-edit>
        </v-dialog>
        <v-navigation-drawer class="mb-4"
                             absolute
                             permanent
                             right
                             :width="sidebarWidth"
                             :style="{right: (sidebarWidth-250)+'px'}"
        >
            <v-data-table class="wells-table"
                          :headers="headers"
                          :items="wells"
                          width="100%"
                          :options="{itemsPerPage: -1}"
                          hide-default-footer
            >
                <template #header.actions>
                    <v-menu>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon>mdi-cog</v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item v-for="header in extraHeaders" :key="header.value">
                                <v-list-item-action>
                                    <v-checkbox v-model="activeHeaders[header.value]"></v-checkbox>
                                </v-list-item-action>

                                <v-list-item-content>
                                    <v-list-item-title>{{header.text}}</v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </template>
                <template #item.imaginary="{ item }">
                    {{item.imaginary ? 'Мнимая' : 'Нет'}}
                </template>
                <template #item.perf_z="{item}">
                    {{item.nodes.map(node => node.z_coord_relative).join(', ')}}
                </template>
                <template #item.perf_p="{item}">
                    {{item.nodes.map(node => node.p_pressure).join(', ')}}
                </template>
                <template #item.perf_sw="{item}">
                    {{item.nodes.map(node => node.sw_water_saturation).join(', ')}}
                </template>
                <template #item.connections="{item}">
                    <div v-for="conn in getAllConnections(item)" :key="conn.value" v-html="conn.text.replace(' ', '&nbsp;')">
                    </div>
                </template>
                <template #item.actions="{ item }">
                    <div class="d-flex flex-nowrap">
                        <v-btn icon @click="$emit('active', item.id)">
                            <v-icon>mdi-eye</v-icon>
                        </v-btn>
                        <v-btn icon @click="editWell(item)">
                            <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon @click="deleteWell(item)">
                            <v-icon>mdi-delete</v-icon>
                        </v-btn>
                    </div>
                </template>
            </v-data-table>
        </v-navigation-drawer>
    </v-sheet>
</template>

<script>
import axios from "axios";
import WellEdit from "@/components/Well/WellEdit";
import {Well} from "@/Well";
import {getAllConnectionsText, getWellConnections} from "@/utils";

export default {
    props: [
        'value', 'gridSize', 'gridCellSize', 'gridFiles', 'gridProperties', 'problem', 'isParsed',
        'nodes', 'connections', 'inputConstants'
    ],
    components: {WellEdit},
    data() {
        return {
            item: null,
            reservoir: {
                grids_x: 0,
                grids_y: 0,
                grids_z: 0,
                dx: 0,
                dy: 0,
                dz: 0,
            },
            editConnections: [],
            editNodes: [],
            wells: [],
            filledWells: [],
            activeHeaders: {
                x_coord: false,
                y_coord: false,
                z_coord: false,
                z_coord_relative: false,
                p: false,
                depth: false,
                connections: false,
            },
            extraHeaders: [
                {text: 'X', value: 'x_coord'},
                {text: 'Y', value: 'y_coord'},
                {text: 'Z', value: 'z_coord'},
                {text: 'Z (отн)', value: 'z_coord_relative'},
                {text: 'Z (отн, перф)', value: 'perf_z'},
                {text: 'P', value: 'p'},
                {text: 'P (перф)', value: 'perf_p'},
                {text: 'Sw', value: 'sw_water_saturation'},
                {text: 'Sw (перф)', value: 'perf_sw'},
                {text: 'Глубина', value: 'depth'},
                {text: 'Перетоки', value: 'connections'},
            ],
            types: [
                {text: 'Добывающая', value: 'producer'},
                {text: 'Нагнетающая', value: 'injector'},
                {text: 'Закрытая', value: 'closed'},
                {text: 'Мнимая', value: 'imaginary'},
                {text: 'Неизвестно', value: 'unknown'}
            ],
            connectionBuilderType: 'insim',
            connectionBuilderTypes: [
                {text: 'Делоне 3D',  value: 'delaunay'},
                {text: 'Делоне 3D (QHull)', value: 'delaunay_qhull'},
                {text: 'Делоне 3D (INSIM)', value: 'insim'},
                {text: 'Послойный Делоне 3D (INSIM)', value: 'insim_layers'},
                {text: 'Делоне 2D',  value: 'delaunay_2d'},
            ],
            imaginaryBuilderType: 'mitchell',
            imaginaryBuilderTypes: [
                {text: 'Митчелл', value: 'mitchell'},
                {text: 'Митчелл (INSIM)', value: 'insim_mitchell'},
                {text: 'Делоне',  value: 'delaunay'},
            ],
            maxWidth: 400,
            maxHeight: 400,
            ignoreWellsUpdate: false,
            defaultWell: {
                "id": null,
                "name": null,
                "type": null,
                "imaginary": false,
                "cell_ij": null,
                "bottomhole_depth": 0,
                "phase": null,
                "nodes": [],
                "path": [],
                "xyz_path": [],
                "x_coord": 0,
                "y_coord": 0,
                "z_coord": 0,
                "k_well_permeability": 0,
                "h_well_net_thickness": 0,
                "sw_water_saturation": 0
            },
            error: false,
            dialog: false,
            editedItem: {},
            editedItemIndex: -1,
            recalcLoading: false,
            imaginaryLoading: false,
            imaginaryCount: 0,
            connectionsMayBeStale: false,
        }
    },
    watch: {
        value() {
            this.initItem();
        },
        connections: {
            deep: true,
            handler() {
                this.initItem();
            }
        },
        nodes: {
            deep: true,
            handler() {
                this.initItem();
            }
        },
        reservoir: {
            deep: true,
            handler() {
                this.updateParentItem();
            }
        },
        wells: {
            deep: true,
            handler() {
                this.updateParentItem();
            }
        },
        loadError() {
            this.$emit('error', this.loadError);
        }
    },
    created() {
        this.initItem();
    },
    methods: {
        initItem() {
            let srcWellsJson = JSON.stringify(this.wells
                ? this.wells.map(well => well.json())
                : []
            );
            let newWellsJson = JSON.stringify(this.value
                    ? this.value.map(well => well.json())
                    : []
            );

            let isSame = srcWellsJson === newWellsJson;
            if (this.reservoir.grids_x !== this.gridSize[0]) {
                isSame = false;
            }

            if (!isSame) {
                this.wells = this.value;
                this.imaginaryCount = this.wells.length;
                this.reservoir = {
                    grids_x: parseInt(this.gridSize[0]),
                    grids_y: parseInt(this.gridSize[1]),
                    grids_z: parseInt(this.gridSize[2]),
                    dx: parseInt(this.gridCellSize[0]),
                    dy: parseInt(this.gridCellSize[1]),
                    dz: parseInt(this.gridCellSize[2]),
                };
            }

            if (this.editConnections !== this.connections) {
                this.editConnections = this.connections;
            }

            if (this.editNodes !== this.nodes) {
                this.editNodes = this.nodes;
            }
        },
        async updateParentItem() {
            if (this.ignoreWellsUpdate) {
                return;
            }

            this.filledWells = null;
            this.$emit('input', this.wells);
        },
        async loadConnections() {
            this.$emit('connections', this.connectionBuilderType);
            this.connectionsMayBeStale = false;
        },
        gridClick({colIndex, rowIndex}) {
            console.log(colIndex, rowIndex);
        },
        deleteEditedWell() {
            if (this.editedItemIndex < 0) {
                return;
            }

            let deletedWell = this.wells[this.editedItemIndex];
            if (deletedWell) {
                this.deleteWell(deletedWell);
            }
        },
        deleteWell(well) {
            this.close();
            let nodeIndex = 0;
            this.$emit('deleteWell', well, nodeIndex);
            this.updateParentItem();
        },
        addWell() {
            this.wells.push(new Well(this.defaultWell, this.grid, this.inputConstants));
            this.updateParentItem();
        },
        async addImaginaryWells() {
            this.imaginaryLoading = true;
            this.error = false;
            try {
                let response = await axios.post('/api/calc/imaginary', {
                    wells: this.wells.map(well => well.json()),
                    method: this.imaginaryBuilderType,
                    file: this.gridFiles,
                    problem: this.problem,
                    grid_properties: this.gridProperties,
                    count: this.imaginaryCount,
                });

                if (response.data.imaginary_wells) {
                    for (let imgWellData of response.data.imaginary_wells) {
                        this.wells.push(new Well(imgWellData, this.grid, this.inputConstants));
                    }
                }

                await this.recalcAll();
            }
            catch (e) {
                this.error = e.toString();
            }
            this.imaginaryLoading = false;
        },
        async fillWells() {
            this.error = false;
            try {
                let response = await axios.post('/api/calc/calc_missing_well_data', {
                    wells: this.wells.map(well => well.json()),
                    file: this.gridFiles,
                    problem: this.problem,
                    grid_properties: this.gridProperties,
                });

                if (response.data.wells) {
                    this.filledWells = [];
                    for (let wellData of response.data.wells) {
                        this.filledWells.push(new Well(wellData, this.grid, this.inputConstants));
                    }
                }
            }
            catch (e) {
                this.error = e.toString();
            }
        },
        async fillWell(well) {
            if (!this.filledWells) {
                await this.fillWells();
            }

            let wellIndex = this.wells.findIndex(searchWell => searchWell.id === well.id);
            let filledWell = this.filledWells.find(searchWell => searchWell.id === well.id);
            if (wellIndex === -1 || !filledWell) {
                return;
            }

            this.wells[wellIndex] = filledWell.clone();
            if (wellIndex === this.editedItemIndex) {
                this.editedItem = this.wells[wellIndex];
            }
        },
        async recalcAll() {
            this.recalcLoading = true;
            await this.fillWells();
            this.wells = this.filledWells.map(well => well.clone());
            this.recalcLoading = false;
        },
        editNew() {
            this.editedItemIndex = -1;
            this.editedItem = new Well({}, this.grid, this.inputConstants);
            this.dialog = true;
        },
        editWell(well) {
            this.editedItemIndex = this.wells.findIndex(searchWell => searchWell.id === well.id);
            if (this.editedItemIndex !== -1) {
                well = this.wells[this.editedItemIndex];
            }
            this.editedItem = well.clone();
            this.dialog = true;
        },
        close() {
            this.dialog = false;
            this.$nextTick(() => {
                this.editedItem = null;
                this.editedItemIndex = -1;
            });
        },
        save() {
            if (this.editedItemIndex > -1) {
                this.$set(this.wells, this.editedItemIndex, this.editedItem);
            }
            else {
                this.wells.push(this.editedItem);
            }

            this.$store.commit('grid/setConnectionsAutoload', false);
            this.connectionsMayBeStale = true;
            this.updateParentItem();
            this.close();
        },
        getAllConnections(fromWell) {
            let allConnections = getAllConnectionsText(fromWell, this.wells, this.nodes);
            let wellConnections = getWellConnections(fromWell, this.connections, this.nodes)
                .map(item => JSON.stringify(item));

            return allConnections
                    .filter(item => wellConnections.includes(item.value));
        },
        copyWellsFrom(srcItem) {
            this.$store.commit('grid/setConnectionsAutoload', false);
            this.editConnections = srcItem?.state?.editConnections || [];
            this.editNodes = srcItem?.state?.editNodes || [];
            this.wells = srcItem?.state?.wells?.map(wellData => new Well(wellData, this.grid, this.inputConstants)) || [];

            this.updateParentItem();
        },
        setUpdatedConnections(connections) {
            debugger;
            this.$emit('input-connections', connections);
        }
    },
    computed: {
        grid() {
            return this.gridProperties;
        },
        isConnectionsLoading(){
            return this.$store.state.grid.connectionsLoading;
        },
        activeBuilderTitle() {
            let activeBuilder = this.connectionBuilderTypes.find(item => item.value === this.connectionBuilderType);
            return activeBuilder ? activeBuilder.text : '-';
        },
        activeImaginaryBuilderTitle() {
            let activeImaginaryBuilder = this.imaginaryBuilderTypes.find(item => item.value === this.imaginaryBuilderType);
            return activeImaginaryBuilder ? activeImaginaryBuilder.text : '-';
        },
        loadError() {
            let error = this.$store.state.grid.connectionsError || null;
            if (this.error) {
                error = this.error;
            }

            if (error && error.length > 100) {
                error = error.substring(0, 100) + '...';
            }

            return error;
        },
        showConnectionsUpdate() {
            return this.connectionsMayBeStale && !this.$store.state.grid.connectionsAutoload;
        },
        containerWidth() {
            return this.wrapperWidth - this.sidebarWidth;
        },
        containerHeight() {
            return this.wrapperHeight;
        },
        headers() {
            let headers = [
                {text: 'Код', value: 'id'},
            ];

            for (let extraHeader of this.extraHeaders) {
                if (this.activeHeaders[extraHeader.value]) {
                    headers.push(extraHeader);
                }
            }

            headers.push({text: 'Действия', value: 'actions', sortable: false});

            return headers;
        },
        sidebarWidth() {
            let width = 250 + (this.headers.length - 2) * 100;
            if (this.activeHeaders.connections) {
                width += 100;
            }

            return width;
        }
    }
}
</script>

<style scoped lang="scss">
.wells-table {
    width: 100%;
}
</style>
